import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  Search, 
  MessageSquareWarning, 
  Globe, 
  FileText, 
  HelpCircle, 
  BookOpen, 
  CreditCard, 
  LayoutDashboard, 
  Bell, 
  Users, 
  Settings, 
  Menu, 
  X, 
  AlertCircle,
  LifeBuoy,
  Sparkles,
  UserCircle,
  LogOut,
  ChevronDown
} from 'lucide-react';
import { useStore } from '../../store/useStore';

export const Navbar: React.FC = () => {
  const { user, unresolvedAlertsCount, logout, openAiModal, switchUserRole } = useStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/80">
      {/* Top Emergency & Trust Alert Bar */}
      <div className="bg-gradient-to-r from-emerald-950 via-slate-900 to-navy-900 border-b border-emerald-900/40 px-4 py-1 text-xs text-slate-300">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 font-semibold text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              AUSTRALIAN IDENTITY DEFENCE
            </span>
            <span className="text-slate-500 hidden sm:inline">|</span>
            <span className="text-slate-400 hidden sm:inline">Privacy-first monitoring. No passwords required.</span>
          </div>

          <div className="flex items-center gap-4 text-xs font-medium">
            <button 
              onClick={openAiModal}
              className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Ask AI Recovery Assistant</span>
            </button>
            <Link 
              to="/identity-theft" 
              className="inline-flex items-center gap-1 text-rose-400 hover:text-rose-300 transition-colors font-semibold"
            >
              <LifeBuoy className="w-3.5 h-3.5" />
              <span>Identity Theft Emergency?</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Nav Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-900/20 group-hover:scale-105 transition-transform">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  <span className="text-lg font-bold tracking-tight text-white font-sans">IDFraud</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/80 text-emerald-300 border border-emerald-700/60 font-semibold font-mono">.au</span>
                </div>
                <span className="text-[10px] text-slate-400 font-medium tracking-wide uppercase">Identity Security Centre</span>
              </div>
            </Link>
          </div>

          {/* Desktop Primary Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/') ? 'text-white bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              Home
            </Link>
            <Link
              to="/check-identity"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/check-identity') ? 'text-white bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              Check My Identity
            </Link>
            <Link
              to="/scam-checker"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/scam-checker') ? 'text-white bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              Scam Checker
            </Link>
            <Link
              to="/website-checker"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/website-checker') ? 'text-white bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              Website Checker
            </Link>
            <Link
              to="/identity-documents"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/identity-documents') ? 'text-white bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              ID Protection
            </Link>
            <Link
              to="/how-it-works"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/how-it-works') ? 'text-white bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              How It Works
            </Link>
            <Link
              to="/resources"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/resources') ? 'text-white bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              Resources
            </Link>
            <Link
              to="/pricing"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/pricing') ? 'text-white bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              Pricing
            </Link>
          </nav>

          {/* Desktop Right Action Area */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-2">
                {/* Alerts Pill */}
                <Link
                  to="/alerts"
                  className="relative p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
                  title="Alerts"
                >
                  <Bell className="w-5 h-5" />
                  {unresolvedAlertsCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-amber-500 text-slate-950 font-bold text-[10px] flex items-center justify-center">
                      {unresolvedAlertsCount}
                    </span>
                  )}
                </Link>

                {/* Dashboard Button */}
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium shadow-md shadow-emerald-950/40 transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>

                {/* User Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg hover:bg-slate-900 text-slate-300 text-sm border border-slate-800"
                  >
                    <img
                      src={user.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&auto=format&fit=crop&q=80'}
                      alt={user.firstName}
                      className="w-6 h-6 rounded-full object-cover border border-emerald-500"
                    />
                    <span className="font-medium text-slate-200">{user.firstName}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </button>

                  {userDropdownOpen && (
                    <div 
                      className="absolute right-0 mt-2 w-56 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl py-2 z-50"
                      onMouseLeave={() => setUserDropdownOpen(false)}
                    >
                      <div className="px-4 py-2 border-b border-slate-800">
                        <p className="text-xs text-slate-400 font-medium">Signed in as</p>
                        <p className="text-sm font-semibold text-white truncate">{user.email}</p>
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-slate-800 text-emerald-400">
                          {user.role}
                        </span>
                      </div>

                      <Link
                        to="/dashboard"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800"
                      >
                        <LayoutDashboard className="w-4 h-4 text-emerald-400" />
                        Dashboard
                      </Link>
                      <Link
                        to="/security-score"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800"
                      >
                        <ShieldCheck className="w-4 h-4 text-teal-400" />
                        Security Score
                      </Link>
                      <Link
                        to="/alerts"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 justify-between"
                      >
                        <span className="flex items-center gap-2.5">
                          <Bell className="w-4 h-4 text-amber-400" />
                          Alerts
                        </span>
                        {unresolvedAlertsCount > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-amber-500/20 text-amber-300 font-bold">
                            {unresolvedAlertsCount}
                          </span>
                        )}
                      </Link>
                      <Link
                        to="/family"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800"
                      >
                        <Users className="w-4 h-4 text-indigo-400" />
                        Family Protection
                      </Link>
                      <Link
                        to="/id-watermark"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800"
                      >
                        <FileText className="w-4 h-4 text-blue-400" />
                        ID Watermark Tool
                      </Link>
                      <Link
                        to="/account-security"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800"
                      >
                        <ShieldCheck className="w-4 h-4 text-cyan-400" />
                        Account Security Audit
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800"
                      >
                        <Settings className="w-4 h-4 text-slate-400" />
                        Settings
                      </Link>
                      <Link
                        to="/admin"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-purple-300 hover:text-purple-200 hover:bg-purple-950/40 border-t border-slate-800"
                      >
                        <Settings className="w-4 h-4 text-purple-400" />
                        Admin Portal
                      </Link>

                      <div className="border-t border-slate-800 pt-1">
                        <button
                          onClick={() => {
                            switchUserRole(user.role === 'admin' ? 'user' : 'admin');
                            setUserDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-1.5 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                        >
                          Switch role to: {user.role === 'admin' ? 'Regular User' : 'Admin'}
                        </button>
                        <button
                          onClick={() => {
                            logout();
                            setUserDropdownOpen(false);
                            navigate('/');
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-rose-400 hover:bg-rose-950/30"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="px-3.5 py-1.5 text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/check-identity"
                  className="px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg shadow-md shadow-emerald-950/30 transition-colors"
                >
                  Check My Identity
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu trigger */}
          <div className="flex items-center gap-2 lg:hidden">
            {user && (
              <Link
                to="/alerts"
                className="relative p-2 rounded-lg text-slate-300 hover:text-white"
              >
                <Bell className="w-5 h-5" />
                {unresolvedAlertsCount > 0 && (
                  <span className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full bg-amber-500 text-slate-950 font-bold text-[9px] flex items-center justify-center">
                    {unresolvedAlertsCount}
                  </span>
                )}
              </Link>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-900"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-slate-900 border-b border-slate-800 px-4 pt-2 pb-6 space-y-2 animate-fade-in">
          <div className="grid grid-cols-2 gap-2 pt-2 pb-2">
            <Link
              to="/check-identity"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-emerald-600 text-white text-center"
            >
              <Search className="w-3.5 h-3.5" />
              Check Identity
            </Link>
            <Link
              to="/scam-checker"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-slate-800 text-slate-200 text-center"
            >
              <MessageSquareWarning className="w-3.5 h-3.5 text-amber-400" />
              Scam Checker
            </Link>
          </div>

          <div className="border-t border-slate-800 pt-2 space-y-1">
            <Link
              to="/dashboard"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              Dashboard
            </Link>
            <Link
              to="/security-score"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              Security Score (82/100)
            </Link>
            <Link
              to="/alerts"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              <span>Alerts</span>
              {unresolvedAlertsCount > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-300 font-bold">
                  {unresolvedAlertsCount} active
                </span>
              )}
            </Link>
            <Link
              to="/website-checker"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              Website Safety Checker
            </Link>
            <Link
              to="/identity-theft"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-rose-300 hover:bg-rose-950/40"
            >
              Identity Theft Emergency Centre
            </Link>
            <Link
              to="/identity-documents"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              Australian ID Protection Guides
            </Link>
            <Link
              to="/id-watermark"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              ID Watermark Generator
            </Link>
            <Link
              to="/account-security"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              Account Security Audit
            </Link>
            <Link
              to="/family"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              Family Protection
            </Link>
            <Link
              to="/pricing"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              Pricing & Plans
            </Link>
            <Link
              to="/resources"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              Resources & Education
            </Link>
            <Link
              to="/admin"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-purple-300 hover:bg-purple-950/40"
            >
              Admin Dashboard
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
